import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Check, Copy, ExternalLink, Clock, AlertCircle, Wallet, Download, QrCode, Save, UserPlus, X, Gift } from 'lucide-react';
import QRCode from 'react-qr-code';
import { database, ref, set, get, update } from '../../firebase';
import { saveScratchHistory, fetchScratchMinRewards } from '../../utils/scratchCardService';

const AddFund = () => {
  const navigate = useNavigate();
  const [usdAmount, setUsdAmount] = useState('');
  const [paymentCurrency, setPaymentCurrency] = useState('');
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentId, setPaymentId] = useState('');
  const [copied, setCopied] = useState(false);
  const [userFundBalance, setUserFundBalance] = useState(0);
  const [userAddedFund, setUserAddedFund] = useState(0);
  const [userFundCount, setUserFundCount] = useState(0);
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [userDbKey, setUserDbKey] = useState('');
  const [timeLeft, setTimeLeft] = useState(12 * 60 * 60);
  const [scanCount, setScanCount] = useState(0);
  const [copyCount, setCopyCount] = useState(0);
  const [lastScanned, setLastScanned] = useState(null);
  const [paymentStartTime, setPaymentStartTime] = useState(null);
  const [sponsorId, setSponsorId] = useState('');
  const [currencyStatementStatus, setCurrencyStatementStatus] = useState({});
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogType, setDialogType] = useState('success');
  const [showScratchDialog, setShowScratchDialog] = useState(false);
  const [scratchRewardAmount, setScratchRewardAmount] = useState(0);
  const [scratchReferenceId, setScratchReferenceId] = useState('');
  const [scratchLoading, setScratchLoading] = useState(false);
  const [fundScratchMinReward, setFundScratchMinReward] = useState(1);

  // Default currency statement structure - BNB REMOVED
  const defaultCurrencyStatement = {
    Bitcoin: 'active',
    'USDT (BEP-20)': 'active',
    'USDT (TRC-20)': 'active',
    Dogecoin: 'active',
    Ethereum: 'active',
    'Shiba Inu': 'active',
    Solana: 'active',
    TRON: 'active',
    USDC: 'active'
  };

  // NOWPayments API Configuration
  const API_KEY = 'NJVV01R-MT7428W-GSQT32M-PA7B8QA';
  const BASE_URL = 'https://api.nowpayments.io/v1';
  const PAYMENT_WINDOW_MS = 12 * 60 * 60 * 1000;
  const FUND_SCRATCH_MIN_REWARD = fundScratchMinReward;

  // Android parity: POST /v1/payment with Map<String, String>
  const createNOWPayment = async (requestMap) => {
    const response = await fetch(`${BASE_URL}/payment`, {
      method: 'POST',
      headers: {
        'x-api-key': API_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestMap)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Payment creation failed: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    return {
      payment_id: data.payment_id || '',
      pay_address: data.pay_address || data.deposit_address || '',
      pay_currency: data.pay_currency || '',
      invoice_url: data.invoice_url || null,
      pay_amount: Number(data.pay_amount || 0),
      actually_paid: data.actually_paid != null ? Number(data.actually_paid) : null,
      price_amount: Number(data.price_amount || 0),
      actually_paid_at_fiat: data.actually_paid_at_fiat != null ? Number(data.actually_paid_at_fiat) : null,
      payment_status: data.payment_status || 'waiting',
      outcome_amount: data.outcome_amount != null ? Number(data.outcome_amount) : null,
      outcome_currency: data.outcome_currency || null,
      raw: data
    };
  };

  // Android parity: GET /v1/payment/{payment_id}
  const getNOWPaymentStatus = async (paymentIdToCheck) => {
    const response = await fetch(`${BASE_URL}/payment/${paymentIdToCheck}`, {
      headers: { 'x-api-key': API_KEY }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to check payment status: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    return {
      payment_id: data.payment_id || paymentIdToCheck,
      payment_status: data.payment_status || 'waiting',
      pay_address: data.pay_address || data.deposit_address || '',
      pay_amount: Number(data.pay_amount || 0),
      pay_currency: data.pay_currency || '',
      actually_paid: data.actually_paid != null ? Number(data.actually_paid) : null,
      actually_paid_at_fiat: data.actually_paid_at_fiat != null ? Number(data.actually_paid_at_fiat) : null,
      price_amount: Number(data.price_amount || 0),
      outcome_amount: data.outcome_amount != null ? Number(data.outcome_amount) : null,
      outcome_currency: data.outcome_currency || null,
      raw: data
    };
  };

  // Initialize user data and check for expired payments
  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    const storedUserName = localStorage.getItem('userName');
    const storedUserDbKey = localStorage.getItem('dbKey');
    const storedSponsorId = localStorage.getItem('sponsorId');
    
    if (storedUserId) {
      setUserId(storedUserId);
      setUserName(storedUserName || 'User');
      setUserDbKey(storedUserDbKey || storedUserId);
      setSponsorId(storedSponsorId || storedUserId);
      
      // Get fund data from localStorage or default
      const storedFundBalance = parseFloat(localStorage.getItem('userFundBalance') || 0);
      const storedAddedFund = parseFloat(localStorage.getItem('userAddedFund') || 0);
      const storedFundCount = parseInt(localStorage.getItem('userFundCount') || 0);
      
      setUserFundBalance(storedFundBalance);
      setUserAddedFund(storedAddedFund);
      setUserFundCount(storedFundCount);
      
      // Fetch current fund data and currency statement from database
      fetchCurrentUserData(storedUserDbKey || storedUserId);
    }

    // Check for expired payments on page load
    checkForExpiredPayments();
    fetchScratchSettings();
  }, []);

  const fetchScratchSettings = async () => {
    try {
      const settings = await fetchScratchMinRewards();
      setFundScratchMinReward(Number(settings.fund_add) || 1);
    } catch (error) {
      console.error('Error fetching scratch minimum settings:', error);
    }
  };

  // Fetch current user data including fund and currency statement
  const fetchCurrentUserData = async (dbKey) => {
    try {
      if (!dbKey) return;
      
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (snapshot.exists()) {
        const usersByDate = snapshot.val();
        
        for (const date in usersByDate) {
          const users = usersByDate[date];
          
          if (users && users[dbKey]) {
            const userData = users[dbKey];
            
            // Get fund data
            const fundData = userData.fund || {};
            const currentFundBalance = parseFloat(fundData.balance) || 0;
            const currentAddedFund = parseFloat(fundData.total) || 0;
            const currentFundCount = parseInt(fundData.count) || 0;
            
            setUserFundBalance(currentFundBalance);
            setUserAddedFund(currentAddedFund);
            setUserFundCount(currentFundCount);
            
            localStorage.setItem('userFundBalance', currentFundBalance.toString());
            localStorage.setItem('userAddedFund', currentAddedFund.toString());
            localStorage.setItem('userFundCount', currentFundCount.toString());

            // Check and update currency statement status
            await checkAndUpdateCurrencyStatement(userData, date, dbKey);
            
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching current user data:', error);
    }
  };

  // Check if currency statement exists and create if not
  const checkAndUpdateCurrencyStatement = async (userData, dateKey, dbKey) => {
    try {
      // Check if currencyStatementStatus exists in user data
      if (!userData.currencyStatementStatus) {
        console.log('currencyStatementStatus not found, creating default structure...');
        
        const timestamp = new Date().toISOString();
        
        // Create the currency statement structure
        const currencyStatementData = {
          currencyStatementStatus: defaultCurrencyStatement,
          currencyStatementStatusUpdated: timestamp,
          currencyStatementStatusUpdatedBy: 'system'
        };
        
        // Update in Firebase
        const userRef = ref(database, `users/${dateKey}/${dbKey}`);
        await update(userRef, currencyStatementData);
        
        console.log('Default currencyStatementStatus created successfully');
        setCurrencyStatementStatus(defaultCurrencyStatement);
        
        // Update available currencies based on active status
        updateAvailableCurrencies(defaultCurrencyStatement);
        
      } else {
        console.log('currencyStatementStatus already exists');
        setCurrencyStatementStatus(userData.currencyStatementStatus);
        
        // Update available currencies based on active status
        updateAvailableCurrencies(userData.currencyStatementStatus);
      }
    } catch (error) {
      console.error('Error checking/updating currency statement:', error);
    }
  };

  // Update available currencies based on active status - BNB REMOVED
  const updateAvailableCurrencies = useCallback((currencyStatus) => {
    const activeCurrencies = [];
    
    Object.entries(currencyStatus).forEach(([currency, status]) => {
      if (status === 'active') {
        // Map display names to currency codes used in payment
        const currencyMap = {
          'Bitcoin': 'BTC',
          'USDT (BEP-20)': 'USDTBSC',
          'USDT (TRC-20)': 'USDTTRC20',
          'Dogecoin': 'DOGE',
          'Ethereum': 'ETH',
          'Shiba Inu': 'SHIB',
          'Solana': 'SOL',
          'TRON': 'TRX',
          'USDC': 'USDC'
        };
        
        if (currencyMap[currency]) {
          activeCurrencies.push(currencyMap[currency]);
        }
      }
    });
    
    setAvailableCurrencies(activeCurrencies);
    
    // Don't auto-select currency anymore
    setPaymentCurrency('');
    
  }, []);

  // Check for expired payments on page load/refresh
  const checkForExpiredPayments = async () => {
    const savedPaymentData = localStorage.getItem('activePayment');
    if (savedPaymentData) {
      const payment = JSON.parse(savedPaymentData);
      const currentTime = Date.now();
      const expiryTime = payment.expiryTime || payment.startTime + PAYMENT_WINDOW_MS;
      
      if (currentTime > expiryTime) {
        // Payment expired - save to UserFundTransactions as expired
        await savePaymentStatusToDatabase({
          ...payment,
          status: 'expired',
          reason: 'Payment expired due to timeout'
        });
        setPaymentStatus('expired');
        setPaymentId(payment.paymentId);
        setUsdAmount(payment.amount);
        setPaymentCurrency(payment.currency);
        showDialogMessage('Payment Expired', 'Your payment session has expired. Please create a new payment.', 'error');
        localStorage.removeItem('activePayment');
      } else {
        // Payment still active - restore it
        const timeLeftSeconds = Math.floor((expiryTime - currentTime) / 1000);
        setPaymentStatus('pending');
        setPaymentId(payment.paymentId);
        setUsdAmount(payment.amount);
        setPaymentCurrency(payment.currency);
        setPaymentStartTime(payment.startTime);
        setTimeLeft(timeLeftSeconds);
        setScanCount(payment.scanCount || 0);
        setCopyCount(payment.copyCount || 0);
        
        // Fetch payment details
        fetchPaymentDetails(payment.paymentId);
      }
    }
  };

  // Save payment status to UserFundTransactions under sponsor ID
  const savePaymentStatusToDatabase = async (paymentData) => {
    try {
      const date = paymentData.transactionDate || new Date().toISOString().split('T')[0];
      const transactionId = paymentData.paymentId || `PAYMENT_${Date.now()}`;
      const sponsorIdToUse = sponsorId || userId || 'unknown';
      
      const transactionRef = ref(database, `UserFundTransactions/${date}/${sponsorIdToUse}/${transactionId}`);
      const status = String(paymentData.status || 'pending').toLowerCase();
      const outcomeAmount = paymentData.outcome_amount != null ? Number(paymentData.outcome_amount) : null;
      const actuallyPaidFiat = paymentData.actually_paid_at_fiat != null ? Number(paymentData.actually_paid_at_fiat) : null;
      const enteredAmount = parseFloat(paymentData.amount);
      const effectiveAmount =
        (status === 'completed' || status === 'success' || status === 'finished') && outcomeAmount > 0
          ? outcomeAmount
          : Number.isFinite(enteredAmount)
            ? enteredAmount
            : actuallyPaidFiat > 0
              ? actuallyPaidFiat
              : 0;
      
      const transactionRecord = {
        transactionId: transactionId,
        paymentId: paymentData.paymentId || 'N/A',
        timestamp: Date.now(),
        timestampFormatted: new Date().toISOString(),
        userId: userId,
        userName: userName,
        memberId: userDbKey,
        sponsorId: sponsorIdToUse,
        amount: effectiveAmount,
        currency: paymentData.currency || paymentCurrency,
        displayCurrency: getCurrencyDisplayName(paymentData.currency || paymentCurrency),
        network: getNetworkInfo(paymentData.currency || paymentCurrency),
        status: paymentData.status || 'pending',
        reason: paymentData.reason || '',
        type: 'payment',
        source: 'Aerovolt',
        wallet: 'Fund Wallet',
        startTime: paymentData.startTime || Date.now(),
        expiryTime: paymentData.expiryTime || (paymentData.startTime + PAYMENT_WINDOW_MS),
        scanCount: paymentData.scanCount || 0,
        copyCount: paymentData.copyCount || 0,
        completedTime: paymentData.completedTime || null,
        payinAddress: paymentData.payinAddress || paymentData.pay_address || paymentData.deposit_address || '',
        payAddress: paymentData.payinAddress || paymentData.pay_address || paymentData.deposit_address || '',
        pay_amount: paymentData.pay_amount != null ? Number(paymentData.pay_amount) : null,
        pay_currency: paymentData.pay_currency || null,
        price_amount: paymentData.price_amount != null ? Number(paymentData.price_amount) : null,
        outcome_amount: outcomeAmount,
        outcome_currency: paymentData.outcome_currency || null,
        actually_paid: paymentData.actually_paid != null ? Number(paymentData.actually_paid) : null,
        actually_paid_at_fiat: actuallyPaidFiat
      };
      
      await set(transactionRef, transactionRecord);
      console.log(`Payment status saved to UserFundTransactions/${date}/${sponsorIdToUse}/${transactionId}`);
      return transactionId;
    } catch (error) {
      console.error('Error saving payment status:', error);
    }
  };

  // Show dialog function
  const showDialogMessage = (title, message, type = 'success') => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogType(type);
    setShowDialog(true);
  };

  // Close dialog
  const closeDialog = () => {
    setShowDialog(false);
    // Only navigate to fundhistory if it's a successful payment dialog
    if (dialogType === 'success' && dialogTitle === 'Payment Successful') {
      navigate('/user-dashboard/fundhistory');
    }
  };

  // Generate scratch reward based on admin minimum setting
  const generateScratchReward = () => {
    return FUND_SCRATCH_MIN_REWARD;
  };

  // Fetch payment details
  const fetchPaymentDetails = async (id) => {
    try {
      const statusData = await getNOWPaymentStatus(id);
      setPaymentDetails(prev => ({
        ...(prev || {}),
        ...(statusData.raw || {}),
        payment_id: statusData.payment_id,
        payment_status: statusData.payment_status,
        pay_amount: statusData.pay_amount,
        pay_currency: statusData.pay_currency,
        actually_paid: statusData.actually_paid,
        actually_paid_at_fiat: statusData.actually_paid_at_fiat,
        price_amount: statusData.price_amount,
        outcome_amount: statusData.outcome_amount,
        outcome_currency: statusData.outcome_currency
      }));
    } catch (error) {
      console.error('Error fetching payment details:', error);
    }
  };

  // Timer for payment expiration
  useEffect(() => {
    let timer;
    if (paymentStatus === 'pending' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timer);
            
            // Mark payment as expired and save to database
            const savedPaymentData = localStorage.getItem('activePayment');
            if (savedPaymentData) {
              const payment = JSON.parse(savedPaymentData);
              savePaymentStatusToDatabase({
                ...payment,
                status: 'expired',
                reason: 'Payment expired due to timeout'
              });
            }
            
            setPaymentStatus('expired');
            localStorage.removeItem('activePayment');
            showDialogMessage('Payment Expired', 'Your payment session has expired. Please create a new payment.', 'error');
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [paymentStatus, timeLeft]);

  // Poll for payment status
  useEffect(() => {
    let interval;
    if (paymentId && paymentStatus === 'pending') {
      interval = setInterval(() => {
        checkPaymentStatus(paymentId);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [paymentId, paymentStatus]);

  // Map currency codes for NOWPayments - BNB REMOVED
  const getNOWPaymentsCurrencyCode = (currency) => {
    const currencyMap = {
      'BTC': 'btc',
      'USDTBSC': 'usdtbsc',
      'USDTTRC20': 'usdttrc20',
      'DOGE': 'doge',
      'ETH': 'eth',
      'SHIB': 'shib',
      'SOL': 'sol',
      'TRX': 'trx',
      'USDC': 'usdc'
    };
    return currencyMap[currency] || currency.toLowerCase();
  };

  // Get display name for currency - BNB REMOVED
  const getCurrencyDisplayName = (currency) => {
    const displayNames = {
      'BTC': 'Bitcoin',
      'USDTBSC': 'USDT (BEP-20)',
      'USDTTRC20': 'USDT (TRC-20)',
      'DOGE': 'Dogecoin',
      'ETH': 'Ethereum',
      'SHIB': 'Shiba Inu',
      'SOL': 'Solana',
      'TRX': 'TRON',
      'USDC': 'USDC'
    };
    return displayNames[currency] || currency;
  };

  // Get network info - BNB REMOVED
  const getNetworkInfo = (currency) => {
    const networkInfo = {
      'BTC': 'Bitcoin Network',
      'USDTBSC': 'Binance Smart Chain (BEP-20)',
      'USDTTRC20': 'TRON Network (TRC-20)',
      'DOGE': 'Dogecoin Network',
      'ETH': 'Ethereum Network (ERC-20)',
      'SHIB': 'Ethereum Network (ERC-20)',
      'SOL': 'Solana Network',
      'TRX': 'TRON Network',
      'USDC': 'Ethereum Network (ERC-20)'
    };
    return networkInfo[currency] || 'Main Network';
  };

  // Create fund transaction record for successful payment
  const createFundTransactionRecord = async (userData, amount, previousBalance, newBalance, newAdded, newCount, paymentMeta = {}) => {
    try {
      const transactionId = `FUND_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const date = new Date().toISOString().split('T')[0];
      const sponsorIdToUse = sponsorId || userId || 'unknown';
      
      const transactionRef = ref(database, `UserFundTransactions/${date}/${sponsorIdToUse}/${transactionId}`);
      
      const transactionRecord = {
        transactionId: transactionId,
        timestamp: Date.now(),
        timestampFormatted: new Date().toISOString(),
        userId: userData.sponsorId,
        userName: userData.name,
        memberId: userDbKey,
        sponsorId: sponsorIdToUse,
        amount: amount,
        type: 'User Added',
        source: 'Aerovolt',
        description: `User added funds via ${getCurrencyDisplayName(paymentCurrency)}`,
        wallet: 'Fund Wallet',
        previousFundBalance: previousBalance,
        fundBalance: newBalance,
        userAddedFund: newAdded,
        fundCount: newCount,
        lastFundAmount: amount,
        lastFundDate: new Date().toISOString(),
        paymentMethod: paymentCurrency,
        paymentDisplayMethod: getCurrencyDisplayName(paymentCurrency),
        paymentId: paymentId,
        network: getNetworkInfo(paymentCurrency),
        status: 'completed',
        scanCount: scanCount,
        copyCount: copyCount,
        payinAddress: paymentMeta.payinAddress || paymentMeta.pay_address || paymentDetails?.pay_address || paymentDetails?.deposit_address || '',
        payAddress: paymentMeta.payinAddress || paymentMeta.pay_address || paymentDetails?.pay_address || paymentDetails?.deposit_address || '',
        pay_amount: paymentMeta.pay_amount != null ? Number(paymentMeta.pay_amount) : null,
        pay_currency: paymentMeta.pay_currency || null,
        price_amount: paymentMeta.price_amount != null ? Number(paymentMeta.price_amount) : null,
        outcome_amount: paymentMeta.outcome_amount != null ? Number(paymentMeta.outcome_amount) : null,
        outcome_currency: paymentMeta.outcome_currency || null,
        actually_paid: paymentMeta.actually_paid != null ? Number(paymentMeta.actually_paid) : null,
        actually_paid_at_fiat: paymentMeta.actually_paid_at_fiat != null ? Number(paymentMeta.actually_paid_at_fiat) : null
      };
      
      await set(transactionRef, transactionRecord);
      console.log(`Fund transaction saved to UserFundTransactions/${date}/${sponsorIdToUse}/${transactionId}`);
      return transactionId;
    } catch (error) {
      console.error('Error creating fund transaction record:', error);
      return null;
    }
  };

  // Update user fund details
  const updateUserFundDetailsInDatabase = async (amount, paymentMeta = {}) => {
    try {
      const amountToAdd = parseFloat(amount);
      
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (snapshot.exists()) {
        const usersByDate = snapshot.val();
        let userFound = null;
        let userDate = '';
        
        for (const date in usersByDate) {
          const users = usersByDate[date];
          
          if (users && users[userDbKey]) {
            userFound = users[userDbKey];
            userDate = date;
            break;
          }
        }
        
        if (userFound) {
          const currentFundData = userFound.fund || {};
          
          const currentFundBalance = parseFloat(currentFundData.balance) || 0;
          const currentAddedFund = parseFloat(currentFundData.total) || 0;
          const currentFundCount = parseInt(currentFundData.count) || 0;
          
          const newFundBalance = currentFundBalance + amountToAdd;
          const newAddedFund = currentAddedFund + amountToAdd;
          const newFundCount = currentFundCount + 1;
          
          const updatedFundData = {
            balance: newFundBalance,
            total: newAddedFund,
            count: newFundCount,
            lastAmount: amountToAdd,
            lastDate: new Date().toISOString(),
            lastTransactionId: paymentId,
            lastTransactionType: 'User Added',
            lastTransactionWallet: 'Fund Wallet',
            lastTransactionSource: 'Aerovolt',
            lastPaymentMethod: paymentCurrency,
            lastPaymentDisplayMethod: getCurrencyDisplayName(paymentCurrency),
            lastPaymentStatus: 'completed'
          };
          
          await update(ref(database, `users/${userDate}/${userDbKey}/fund`), updatedFundData);
          
          await createFundTransactionRecord(
            userFound,
            amountToAdd,
            currentFundBalance,
            newFundBalance,
            newAddedFund,
            newFundCount,
            paymentMeta
          );
          
          setUserFundBalance(newFundBalance);
          setUserAddedFund(newAddedFund);
          setUserFundCount(newFundCount);
          
          localStorage.setItem('userFundBalance', newFundBalance.toString());
          localStorage.setItem('userAddedFund', newAddedFund.toString());
          localStorage.setItem('userFundCount', newFundCount.toString());
          
          return { success: true };
        }
      }
      return { success: false, error: 'User not found' };
    } catch (error) {
      console.error('Error updating user fund details:', error);
      return { success: false, error: error.message };
    }
  };

  const creditScratchRewardToFund = async (rewardAmount, eventType, referenceId) => {
    try {
      const normalizedReward = Number(rewardAmount);
      if (!normalizedReward || normalizedReward <= 0) {
        return { success: false, error: 'Invalid scratch reward amount' };
      }

      const usersRef = ref(database, 'users');
      const usersSnapshot = await get(usersRef);
      if (!usersSnapshot.exists()) {
        return { success: false, error: 'Users data not found' };
      }

      const usersByDate = usersSnapshot.val();
      let userFound = null;
      let userDate = '';

      for (const date in usersByDate) {
        const users = usersByDate[date];
        if (users && users[userDbKey]) {
          userFound = users[userDbKey];
          userDate = date;
          break;
        }
      }

      if (!userFound || !userDate) {
        return { success: false, error: 'User not found for scratch reward' };
      }

      const today = new Date().toISOString().split('T')[0];
      const sponsorIdToUse = sponsorId || userId || 'unknown';
      const safeReference = String(referenceId || Date.now()).replace(/[^a-zA-Z0-9_-]/g, '_');
      const transactionId = `SCRATCH_${eventType}_${safeReference}`;
      const transactionRef = ref(database, `UserFundTransactions/${today}/${sponsorIdToUse}/${transactionId}`);
      const existingTransaction = await get(transactionRef);

      if (existingTransaction.exists()) {
        return {
          success: true,
          alreadyCredited: true,
          rewardAmount: Number(existingTransaction.val()?.amount || normalizedReward)
        };
      }

      const currentFundData = userFound.fund || {};
      const currentFundBalance = parseFloat(currentFundData.balance) || 0;
      const currentAddedFund = parseFloat(currentFundData.total) || 0;
      const currentFundCount = parseInt(currentFundData.count) || 0;
      const newFundBalance = currentFundBalance + normalizedReward;
      const newAddedFund = currentAddedFund + normalizedReward;
      const newFundCount = currentFundCount + 1;

      const updatedFundData = {
        balance: newFundBalance,
        total: newAddedFund,
        count: newFundCount,
        lastAmount: normalizedReward,
        lastDate: new Date().toISOString(),
        lastTransactionId: transactionId,
        lastTransactionType: 'Scratch Reward',
        lastTransactionWallet: 'Fund Wallet',
        lastTransactionSource: 'Scratch Card',
        lastPaymentStatus: 'completed'
      };

      await update(ref(database, `users/${userDate}/${userDbKey}/fund`), updatedFundData);

      await set(transactionRef, {
        transactionId,
        timestamp: Date.now(),
        timestampFormatted: new Date().toISOString(),
        userId: userFound.sponsorId || userId,
        userName: userFound.name || userName,
        memberId: userDbKey,
        sponsorId: sponsorIdToUse,
        amount: normalizedReward,
        type: 'scratch_reward',
        source: 'Scratch Card',
        description: `Scratch card reward for ${eventType.replace('_', ' ')}`,
        wallet: 'Fund Wallet',
        previousFundBalance: currentFundBalance,
        fundBalance: newFundBalance,
        userAddedFund: newAddedFund,
        fundCount: newFundCount,
        status: 'completed',
        referenceId: referenceId || '',
        minimumReward: FUND_SCRATCH_MIN_REWARD
      });

      try {
        await saveScratchHistory({
          dateKey: today,
          userId: userFound.sponsorId || userId,
          userName: userFound.name || userName,
          memberId: userDbKey,
          sponsorId: sponsorIdToUse,
          amount: normalizedReward,
          source: 'Scratch Card',
          eventType,
          description: `Scratch card reward for ${eventType.replace('_', ' ')}`,
          wallet: 'Fund Wallet',
          referenceId: referenceId || '',
          minimumReward: FUND_SCRATCH_MIN_REWARD,
          status: 'completed'
        });
      } catch (scratchHistoryError) {
        console.error('Error saving scratch history entry:', scratchHistoryError);
      }

      setUserFundBalance(newFundBalance);
      setUserAddedFund(newAddedFund);
      setUserFundCount(newFundCount);

      localStorage.setItem('userFundBalance', newFundBalance.toString());
      localStorage.setItem('userAddedFund', newAddedFund.toString());
      localStorage.setItem('userFundCount', newFundCount.toString());

      return { success: true, rewardAmount: normalizedReward };
    } catch (error) {
      console.error('Error crediting scratch reward:', error);
      return { success: false, error: error.message };
    }
  };

  const handleScratchClaim = async () => {
    if (scratchLoading || !scratchRewardAmount) return;

    setScratchLoading(true);
    try {
      const result = await creditScratchRewardToFund(scratchRewardAmount, 'fund_add', scratchReferenceId || paymentId);
      if (result.success) {
        const creditedAmount = Number(result.rewardAmount || scratchRewardAmount);
        setShowScratchDialog(false);
        showDialogMessage(
          'Scratch Reward Added',
          `$${creditedAmount.toFixed(2)} scratch reward has been added to your Fund Wallet.`,
          'success'
        );
        await fetchCurrentUserData(userDbKey);
      } else {
        showDialogMessage('Scratch Reward Failed', result.error || 'Unable to add scratch reward.', 'error');
      }
    } finally {
      setScratchLoading(false);
    }
  };

  const handlePayment = async () => {
    // Current flow: show update popup on every click
    // showDialogMessage('comming soon', 'The fund will enable, please be waited.', 'info');
    // return;

    if (!usdAmount || parseFloat(usdAmount) < 1) {
      showDialogMessage('Invalid Amount', 'Please enter an amount of at least $1', 'error');
      return;
    }
    if (!userId) {
      showDialogMessage('Login Required', 'Please login to continue', 'error');
      return;
    }
    if (!paymentCurrency) {
      showDialogMessage('Currency Required', 'Please select a payment currency', 'error');
      return;
    }
    if (availableCurrencies.length === 0) {
      showDialogMessage('No Currency Available', 'No active currencies available for payment. Please contact admin.', 'error');
      return;
    }

    setLoading(true);
    setPaymentStatus('creating');
    
    try {
      // Show loading for 2 seconds
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const npCurrency = getNOWPaymentsCurrencyCode(paymentCurrency);
      
      console.log('Creating payment with currency:', npCurrency);

      const paymentRequestMap = {
        price_amount: String(parseFloat(usdAmount)),
        price_currency: 'usd',
        pay_currency: String(npCurrency),
        order_id: `ORDER_${userId}_${Date.now()}`,
        order_description: `User added funds for ${userName} (ID: ${userId}) via ${getCurrencyDisplayName(paymentCurrency)}`,
        ipn_callback_url: `${window.location.origin}/payment-callback`,
        success_url: `${window.location.origin}/user-dashboard/addfund?status=success`,
        cancel_url: `${window.location.origin}/user-dashboard/addfund?status=canceled`
      };

      const paymentResult = await createNOWPayment(paymentRequestMap);
      console.log('Payment result:', paymentResult);
      
      const startTime = Date.now();
      const expiryTime = startTime + PAYMENT_WINDOW_MS;
      const transactionDate = new Date(startTime).toISOString().split('T')[0];
      
      setPaymentStartTime(startTime);
      setPaymentDetails(paymentResult.raw || paymentResult);
      setPaymentId(paymentResult.payment_id);
      setPaymentStatus('pending');
      setTimeLeft(Math.floor(PAYMENT_WINDOW_MS / 1000));
      setScanCount(0);
      setCopyCount(0);
      
      // Save payment to localStorage
      const paymentDataToSave = {
        paymentId: paymentResult.payment_id,
        amount: usdAmount,
        currency: paymentCurrency,
        payinAddress: paymentResult.pay_address || paymentResult.deposit_address || '',
        pay_address: paymentResult.pay_address || paymentResult.deposit_address || '',
        pay_amount: paymentResult.pay_amount,
        pay_currency: paymentResult.pay_currency,
        price_amount: paymentResult.price_amount,
        outcome_amount: paymentResult.outcome_amount,
        outcome_currency: paymentResult.outcome_currency,
        actually_paid: paymentResult.actually_paid,
        actually_paid_at_fiat: paymentResult.actually_paid_at_fiat,
        startTime: startTime,
        expiryTime: expiryTime,
        transactionDate,
        scanCount: 0,
        copyCount: 0,
        status: 'pending'
      };
      
      localStorage.setItem('activePayment', JSON.stringify(paymentDataToSave));
      
      // Save pending payment to UserFundTransactions
      await savePaymentStatusToDatabase(paymentDataToSave);
      
      showDialogMessage('Scan the QR', 'Scan to pay the fund to your AV PAY WALLET. Please complete the payment within 12 hours.', 'success');
      
    } catch (error) {
      console.error('Payment creation error:', error);
      setPaymentStatus('failed');
      
      // Save failed payment to UserFundTransactions
      await savePaymentStatusToDatabase({
        paymentId: 'CREATION_FAILED',
        amount: usdAmount,
        currency: paymentCurrency,
        startTime: Date.now(),
        expiryTime: Date.now() + PAYMENT_WINDOW_MS,
        scanCount: 0,
        copyCount: 0,
        status: 'failed',
        reason: error.message
      });
      
      showDialogMessage('Payment Failed', `Payment creation failed: ${error.message}. Please try again.`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (id) => {
    try {
      const statusData = await getNOWPaymentStatus(id);
      const gatewayStatus = String(statusData.payment_status || '').toLowerCase();
      const savedPaymentData = localStorage.getItem('activePayment');

      setPaymentDetails(prev => ({
        ...(prev || {}),
        ...(statusData.raw || {}),
        payment_id: statusData.payment_id,
        payment_status: statusData.payment_status,
        pay_amount: statusData.pay_amount,
        pay_currency: statusData.pay_currency,
        actually_paid: statusData.actually_paid,
        actually_paid_at_fiat: statusData.actually_paid_at_fiat,
        price_amount: statusData.price_amount,
        outcome_amount: statusData.outcome_amount,
        outcome_currency: statusData.outcome_currency
      }));

      if (savedPaymentData && gatewayStatus && !['finished', 'failed', 'expired'].includes(gatewayStatus)) {
        const payment = JSON.parse(savedPaymentData);
        await savePaymentStatusToDatabase({
          ...payment,
          status: gatewayStatus,
          payinAddress: statusData.pay_address || paymentDetails?.pay_address || paymentDetails?.deposit_address || '',
          pay_address: statusData.pay_address || paymentDetails?.pay_address || paymentDetails?.deposit_address || '',
          pay_amount: statusData.pay_amount,
          pay_currency: statusData.pay_currency,
          price_amount: statusData.price_amount,
          outcome_amount: statusData.outcome_amount,
          outcome_currency: statusData.outcome_currency,
          actually_paid: statusData.actually_paid,
          actually_paid_at_fiat: statusData.actually_paid_at_fiat
        });
      }

      if (statusData.payment_status === 'finished') {
        setPaymentStatus('completed');
        
        // Save completed payment to UserFundTransactions
        if (savedPaymentData) {
          const payment = JSON.parse(savedPaymentData);
          await savePaymentStatusToDatabase({
            ...payment,
            status: 'completed',
            completedTime: Date.now(),
            payinAddress: statusData.pay_address || paymentDetails?.pay_address || paymentDetails?.deposit_address || '',
            pay_address: statusData.pay_address || paymentDetails?.pay_address || paymentDetails?.deposit_address || '',
            pay_amount: statusData.pay_amount,
            pay_currency: statusData.pay_currency,
            price_amount: statusData.price_amount,
            outcome_amount: statusData.outcome_amount,
            outcome_currency: statusData.outcome_currency,
            actually_paid: statusData.actually_paid,
            actually_paid_at_fiat: statusData.actually_paid_at_fiat
          });
        }
        
        localStorage.removeItem('activePayment');

        // Credit strictly from gateway-paid values (no entered/default fallback)
        let creditAmount = 0;

        if (statusData.outcome_amount && statusData.outcome_amount > 0) {
          creditAmount = statusData.outcome_amount;
        } else if (statusData.actually_paid_at_fiat && statusData.actually_paid_at_fiat > 0) {
          creditAmount = statusData.actually_paid_at_fiat;
        } else if (statusData.actually_paid && statusData.actually_paid > 0) {
          creditAmount = statusData.actually_paid;
        } else if (statusData.pay_amount && statusData.pay_amount > 0) {
          creditAmount = statusData.pay_amount;
        } else {
          showDialogMessage(
            'Payment Pending Verification',
            'Payment finished but paid amount not received from gateway yet. Please refresh after a moment.',
            'info'
          );
          return;
        }

        const success = await updateUserFundDetailsInDatabase(creditAmount, {
          payinAddress: statusData.pay_address || paymentDetails?.pay_address || paymentDetails?.deposit_address || '',
          pay_address: statusData.pay_address || paymentDetails?.pay_address || paymentDetails?.deposit_address || '',
          pay_amount: statusData.pay_amount,
          pay_currency: statusData.pay_currency,
          price_amount: statusData.price_amount,
          outcome_amount: statusData.outcome_amount,
          outcome_currency: statusData.outcome_currency,
          actually_paid: statusData.actually_paid,
          actually_paid_at_fiat: statusData.actually_paid_at_fiat
        });
        if (success.success) {
          const rewardAmount = generateScratchReward();
          setScratchRewardAmount(rewardAmount);
          setScratchReferenceId(statusData.payment_id || paymentId || `${Date.now()}`);
          setShowScratchDialog(true);

          showDialogMessage(
            'Payment Successful - Scratch Card Unlocked',
            `$${Number(creditAmount).toFixed(2)} has been added to your Fund Balance. Scratch your card now to win extra reward (minimum $${FUND_SCRATCH_MIN_REWARD}).`,
            'success'
          );
          await fetchCurrentUserData(userDbKey);
        }
        
      } else if (statusData.payment_status === 'failed' || statusData.payment_status === 'expired') {
        setPaymentStatus('failed');
        
        // Save failed payment to UserFundTransactions
        if (savedPaymentData) {
          const payment = JSON.parse(savedPaymentData);
          await savePaymentStatusToDatabase({
            ...payment,
            status: 'failed',
            reason: `Payment ${statusData.payment_status}`
          });
        }
        
        localStorage.removeItem('activePayment');
        showDialogMessage('Payment Failed', 'Payment transaction failed. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const getPaymentURI = (paymentData) => {
    const address = paymentData?.pay_address || paymentData?.deposit_address;
    const amount = paymentData?.pay_amount || paymentData?.amount;
    
    if (!address || !amount) return '';
    
    const currency = paymentCurrency.toLowerCase();
    
    switch (currency) {
      case 'btc':
        return `bitcoin:${address}?amount=${amount}`;
      case 'eth':
      case 'shib':
      case 'usdc':
        return `ethereum:${address}?value=${amount}`;
      case 'usdtbsc':
        return `binance:${address}?amount=${amount}`;
      case 'trx':
      case 'usdttrc20':
        return `tron:${address}?amount=${amount}`;
      case 'doge':
        return `dogecoin:${address}?amount=${amount}`;
      case 'sol':
        return `solana:${address}?amount=${amount}`;
      default:
        return address;
    }
  };

  const copyToClipboard = async (text) => {
    if (!text) return;
    
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setCopyCount(prev => prev + 1);
      
      // Update localStorage with new copy count
      const savedPaymentData = localStorage.getItem('activePayment');
      if (savedPaymentData) {
        const payment = JSON.parse(savedPaymentData);
        payment.copyCount = copyCount + 1;
        localStorage.setItem('activePayment', JSON.stringify(payment));
      }
      
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const downloadQRCode = () => {
    if (!paymentDetails?.pay_address) return;
    
    const canvas = document.getElementById('qr-canvas');
    if (!canvas) return;
    
    const link = document.createElement('a');
    link.download = `payment-qr-${paymentId}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const resetPayment = () => {
    setPaymentStatus(null);
    setPaymentDetails(null);
    setPaymentId('');
    setUsdAmount('');
    setTimeLeft(Math.floor(PAYMENT_WINDOW_MS / 1000));
    setScanCount(0);
    setCopyCount(0);
    setLastScanned(null);
    setPaymentStartTime(null);
    localStorage.removeItem('activePayment');
  };

  const cancelPayment = async () => {
    // Save as cancelled payment under sponsor ID in UserFundTransactions
    const savedPaymentData = localStorage.getItem('activePayment');
    if (savedPaymentData) {
      const payment = JSON.parse(savedPaymentData);
      await savePaymentStatusToDatabase({
        ...payment,
        status: 'cancelled',
        reason: 'User cancelled payment'
      });
    }
    
    setPaymentStatus('failed');
    showDialogMessage('Payment Cancelled', 'You have cancelled the payment.', 'error');
    localStorage.removeItem('activePayment');
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const paymentURI = getPaymentURI(paymentDetails);

  return (
    <div className="space-y-4 md:space-y-8 pt-4 max-w-4xl mx-auto px-2 md:px-4">
      {showScratchDialog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="w-full max-w-md rounded-xl border border-yellow-500/40 bg-gradient-to-br from-yellow-900/30 via-[#161E2D] to-emerald-900/30 shadow-2xl">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Gift className="text-yellow-300" size={22} />
                <h3 className="text-xl font-bold text-white">Scratch Card Reward</h3>
              </div>
              <p className="text-gray-200 text-sm mb-4">
                Your fund payment is successful. Scratch now to claim an extra reward. Minimum reward: ${FUND_SCRATCH_MIN_REWARD}.
              </p>
              <div className="rounded-lg border border-dashed border-yellow-300/60 bg-black/20 p-5 text-center mb-4">
                <div className="text-xs uppercase tracking-wider text-yellow-200/90 mb-1">Hidden Reward</div>
                <div className="text-2xl font-bold text-yellow-300">$ {scratchRewardAmount.toFixed(2)}</div>
              </div>
              <button
                onClick={handleScratchClaim}
                disabled={scratchLoading}
                className="w-full py-3 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black font-bold transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {scratchLoading ? 'Claiming Reward...' : 'Scratch & Claim Reward'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-[#161E2D] rounded-xl max-w-md w-full border border-gray-700 shadow-2xl"
            style={{
              background:
                dialogType === 'error'
                  ? 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 50%, #b91c1b 100%)'
                  : 'linear-gradient(135deg, #151C2A 0%, rgb(55, 71, 121) 50%, #234192 100%)'
            }}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">{dialogTitle}</h3>
                <button onClick={closeDialog} className="text-gray-300 hover:text-white transition">
                  <X size={24} />
                </button>
              </div>
              <div className="text-white mb-6">{dialogMessage}</div>
              <div className="flex justify-end">
                <button
                  onClick={closeDialog}
                  className={`px-6 py-2 rounded-lg font-medium transition ${
                    dialogType === 'error' ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                  } text-white`}
                >
                  {dialogType === 'success' && dialogTitle === 'Payment Successful' ? 'View Fund History' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Status Messages */}
      {paymentStatus === 'pending' && (
        <div className="bg-blue-900/40 border border-blue-500 rounded-xl p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="text-blue-300 animate-pulse" size={24} />
              <div>
                <p className="text-lg font-bold text-blue-300">Payment Pending</p>
                <p className="text-blue-400">Time Remaining: {formatTime(timeLeft)}</p>
              </div>
            </div>
            <div className="text-blue-300">
              <p className="text-sm">Payment ID: {paymentId?.slice(-8)}</p>
            </div>
          </div>
        </div>
      )}

      {paymentStatus === 'completed' && (
        <div className="bg-green-900/40 border border-green-500 rounded-xl p-4 md:p-6">
          <div className="flex items-center gap-3">
            <Check className="text-green-400" size={24} />
            <div>
              <p className="text-lg font-bold text-green-300">Payment Successful!</p>
              <p className="text-green-400">${usdAmount} has been added to your Fund Balance</p>
              <p className="text-green-400 text-sm mt-1">New Balance: ${userFundBalance.toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}

      {paymentStatus === 'failed' && (
        <div className="bg-red-900/40 border border-red-500 rounded-xl p-4 md:p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="text-red-400" size={24} />
            <div>
              <p className="text-lg font-bold text-red-300">Payment Failed!</p>
              <p className="text-red-400">Your payment could not be processed.</p>
              <button
                onClick={resetPayment}
                className="mt-2 text-sm bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded transition"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {paymentStatus === 'expired' && (
        <div className="bg-red-900/40 border border-red-500 rounded-xl p-4 md:p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="text-red-400" size={24} />
            <div>
              <p className="text-lg font-bold text-red-300">Payment Expired!</p>
              <p className="text-red-400">Your payment session has expired.</p>
              <button
                onClick={resetPayment}
                className="mt-2 text-sm bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded transition"
              >
                Create New Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Form */}
      {(!paymentStatus || paymentStatus === 'completed' || paymentStatus === 'failed' || paymentStatus === 'expired') && (
        <div className="card-glow p-4 md:p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Add Funds to Your Account</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-foreground mb-2">Amount (USD)</label>
              <input
                type="number"
                value={usdAmount}
                onChange={(e) => setUsdAmount(e.target.value)}
                placeholder="Enter amount (min: $1)"
                min="1"
                step="0.01"
                className="w-full bg-background/80 border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-primary"
                disabled={loading}
              />
            </div>
            
            <div>
              <label className="block text-foreground mb-2">Payment Currency</label>
              <select
                value={paymentCurrency}
                onChange={(e) => setPaymentCurrency(e.target.value)}
                className="w-full bg-background/80 border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-primary"
                disabled={loading || availableCurrencies.length === 0}
              >
                <option value="" className="bg-card">-- Select Currency --</option>
                {availableCurrencies.length > 0 ? (
                  availableCurrencies.map((currency) => (
                    <option key={currency} value={currency} className="bg-card">
                      {getCurrencyDisplayName(currency)}
                    </option>
                  ))
                ) : (
                  <option value="" className="bg-card" disabled>No active currencies available</option>
                )}
              </select>
              
            </div>

            <div className="pt-4">
              <button
                onClick={handlePayment}
                disabled={loading}
                className="w-full btn-electric py-3 px-6 rounded-lg transition text-base flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <UserPlus size={20} />
                    <span>Add Funds</span>
                  </>
                )}
              </button>
              {/* <p className="text-xs text-amber-300 mt-3">
                Add Funds button will work after 12 hours.
              </p> */}
            </div>

            {/* Fund Statistics */}
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
              <div className="text-center">
                <p className="text-muted-foreground text-xs">Current Balance</p>
                <p className="text-green-400 font-bold">${userFundBalance.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground text-xs">Total Added</p>
                <p className="text-blue-400 font-bold">${userAddedFund.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground text-xs">Transactions</p>
                <p className="text-purple-400 font-bold">{userFundCount}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Details */}
      {paymentDetails && paymentStatus === 'pending' && (
        <div className="card-glow p-4 md:p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <QrCode size={24} className="text-primary" />
            Complete Payment
          </h2>
          
          <div className="space-y-6">
            {/* Payment Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-secondary/50 rounded-lg p-3">
                <p className="text-muted-foreground text-xs">Amount to Pay</p>
                <p className="text-foreground font-bold">
                  {paymentDetails.pay_amount || paymentDetails.amount} {paymentCurrency}
                </p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-3">
                <p className="text-muted-foreground text-xs">You Get</p>
                <p className="text-foreground font-bold">${paymentDetails.price_amount} USD</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-3">
                <p className="text-muted-foreground text-xs">Network</p>
                <p className="text-foreground font-bold text-sm">{getNetworkInfo(paymentCurrency)}</p>
              </div>
            </div>

            {/* Payment Address */}
            <div className="bg-secondary/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-muted-foreground text-sm">Send to Address</p>
                <span className="text-blue-400 text-xs px-2 py-1 bg-blue-500/20 rounded">
                  {paymentCurrency}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-background p-3 rounded-lg text-xs font-mono break-all">
                  {paymentDetails.pay_address || paymentDetails.deposit_address}
                </code>
                <button
                  onClick={() => copyToClipboard(paymentDetails.pay_address || paymentDetails.deposit_address)}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground p-3 rounded-lg transition"
                  title="Copy Address"
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                </button>
              </div>
            </div>

            {/* QR Code */}
            <div className="flex flex-col items-center">
              <div className="bg-white p-4 rounded-lg">
                <QRCode
                  id="qr-canvas"
                  value={paymentURI}
                  size={200}
                  level="H"
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={downloadQRCode}
                  className="bg-primary/20 hover:bg-primary/30 text-primary px-4 py-2 rounded-lg transition flex items-center gap-2 text-sm"
                >
                  <Download size={16} />
                  Save QR
                </button>
                <button
                  onClick={() => copyToClipboard(paymentURI)}
                  className="bg-primary/20 hover:bg-primary/30 text-primary px-4 py-2 rounded-lg transition flex items-center gap-2 text-sm"
                >
                  <Copy size={16} />
                  Copy URI
                </button>
              </div>
            </div>

            {/* Cancel Button */}
            <div className="pt-4">
              <button
                onClick={cancelPayment}
                className="w-full bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30 py-3 px-4 rounded-lg transition text-sm"
              >
                Cancel Payment
              </button>
            </div>

            {/* Timer */}
            <div className="text-center text-muted-foreground text-sm">
              <Clock size={14} className="inline mr-1" />
              Time remaining: {formatTime(timeLeft)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddFund;
